import { EmeraldSideNavigation } from '@emerald-react/side-navigation';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  createStyles,
  IconButton,
  makeStyles,
  Theme,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import AdminPanelSettingsIcon from '@material-ui/icons/AdminPanelSettings';
import { createPortal } from 'react-dom';
import {
  Avatar,
  useSidebarPinState,
  Link as BsLink,
  useSidebarOpenState,
} from '@backstage/core-components';
import SidebarLogo from './SidebarLogo';
import { SidebarSearchModal } from '../../search/SidebarSearchModal';
import { SearchModal } from '../../search/SearchModal';
import { useSidebarCloseState } from './providers/SidebarCloseStateProvider';
import useMutationObserver from './hooks/useMutationObserver';
import ProvisioningCenterIcon from '../icons/provisioningCenterIcon';
import PromptStoreIcon from '../icons/promptStoreIcon';
import EllisIcon from '../icons/ellisIcon';
import PermitBuildIcon from '../icons/PermitBuildIcon';
import { AuthContext } from '../../../providers/AuthProvider';
import QEScoreIcon from '../icons/qeScoreIcon';
import QEAgentsIcon from '../icons/qeAgentsIcon';
import IQEAgentMenuIcon from '../icons/iqeAgentMenuIcon';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import GitHubCostCenterIcon from '../icons/gitHubCostCenterIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    '@global': {
      '@media (max-width: 768px)': {
        'main > header': {
          paddingLeft: '74px !important',
        },
      },
      ':root': {
        '--eds-sn-color-primary': theme.palette.sideNavigation.primary,
        '--eds-sn-color-secondary': theme.palette.sideNavigation.secondary,
        '--eds-sn-color-active': theme.palette.sideNavigation.colorActive,
        '--eds-sn-color-background': `${theme.palette.sideNavigation.colorBackground} !important`,
        '--eds-sn-color-border': theme.palette.sideNavigation.colorBorder,
        '--eds-sn-color-hover-background':
          theme.palette.sideNavigation.colorHoverBackground,
        '--eds-sn-color-active-background':
          theme.palette.sideNavigation.colorActiveBackground,
        '--eds-sn-color-icon': theme.palette.sideNavigation.colorIcon,
      },
    },

    rootDiv: {
      height: '100vh',
      '& .emerald-nav-items::-webkit-scrollbar': {
        width: theme.spacing(1.25),
      },
      '& .emerald-side-navigation.collapsed .emerald-nav-items::-webkit-scrollbar':
        {
          width: 0,
        },
      '& .emerald-side-navigation.collapsed .emerald-nav-items': {
        scrollbarWidth: 'none',
        overflow: 'auto',
      },
      '& .emerald-nav-item .emerald-nav-separator': {
        margin: '0px',
      },
      '& .emerald-nav-footer-wrapper': {
        zIndex: 1,
        display: 'flex',
        // padding: '1rem',
        flexDirection: 'column',
        width: '100%',
      },
      '& .username-container': {
        padding: `10px 10px 10px 12px`,
        backgroundColor: theme.palette.bgVariants.variant3,
      },
      '& .pin-icon': {
        bottom: 0,
        top: 'unset !important',
        zIndex: 2,
        padding: '10px 10px 14px 10px !important',
      },

      // '& .collapsed': {
      //   '& .username': {
      //     display: 'none',
      //   },
      // },
    },

    menuButton: {
      display: 'none !important',
      [theme.breakpoints.down(768)]: {
        display: 'block !important',
        position: 'absolute !important',
        top: '30px',
        zIndex: 102,
        left: '24px',
        color: 'white !important',
      },
    },

    leftAvatar: {
      width: 'max-content',
      display: 'flex',
      alignItems: 'center',

      gap: '10px',
      '& div': {
        width: '32px',
        height: '32px',
        fontSize: '14px',
        cursor: 'pointer',

        color: theme.palette.primary.main,
      },
      '& span': {
        fontSize: '14px',
      },
      '&:hover': {
        '& div': {
          backgroundColor: theme.palette.background.tertiary,
          color: theme.palette.primary.main,
        },
      },
    },
    buttonContainer: {
      width: '100%',
      padding: '10px',
    },
    mobileButton: {
      minWidth: '0px',
      padding: '6px',
    },
  }),
);

const buildControlledSideBarItems = (
  controlledItems: any[],
  sidebarFeatures: string[],
  sidebarFeatureChildren: Record<string, string[]>,
) =>
  controlledItems
    .map((item: any) => {
      const itemLabel = item.label || item.name;

      // skip if parent item isn't enabled
      if (!sidebarFeatures.includes(itemLabel)) return null;

      // if no children, keep as-is
      if (!item.children || item.children.length === 0) return item;

      // Filter children
      const children = item.children.filter((child: any) =>
        sidebarFeatureChildren[itemLabel]?.includes(child.label || child.name),
      );

      return children?.length > 0 ? { ...item, children } : null;
    })
    .filter(Boolean);

const SideNav = ({
  isAdminFlag,
  userName,
  profile,
}: {
  isAdminFlag: boolean | null;
  userName: string;
  profile: any;
}) => {
  const classes = useStyles();
  const userContext = useContext(AuthContext);

  const { isPinned, toggleSidebarPinState, isMobile } = useSidebarPinState();
  const [sidebarPinned, setSidebarPinned] = useState(isPinned);
  const [devxHeader, setDevxHeader] = useState<Element | null>(null);
  const location = useLocation();

  // Query devxHeader after page loads and retry if not found
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;

    const findHeader = () => {
      const header =
        document.querySelector('.emerald-app-content .HeaderTopTabs header') ||
        document.querySelector('.emerald-app-content main header');

      if (header) {
        setDevxHeader(header);
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(findHeader, 100);
      }
    };

    findHeader();
  }, [location.pathname]);

  /* All Assets Group */
  const allAssetsGroup = {
    label: 'Reusable Code/API',
    icon: 'code',
    children: [
      {
        label: 'Components',
        link: '/code-library',
        icon: 'widgets',
        linkComponent: Link,
      },
      {
        label: 'All APIs',
        link: '/API',
        icon: 'api',
        linkComponent: Link,
      },
      {
        label: 'Register An Api',
        link: '/API/register-api',
        icon: 'add_circle_outline',
        linkComponent: Link,
      },
      {
        label: 'Storybook',
        link: 'https://docs.emerald.cbre.com/?path=/docs/introduction--docs',
        icon: 'menu_book',
        linkTarget: '_blank',
      },
      {
        label: 'Software Templates',
        link: '/software-templates',
        icon: 'description',
        linkComponent: Link,
      },
    ],
  };

  /* Developer Utilities */
  const developerUtilities = {
    label: 'Dev Tools',
    icon: 'developer_board',

    children: [
      {
        icon: 'code',
        link: '/api-mock',
        label: 'Mock APIs',
        linkComponent: Link,
      },
      {
        link: '/utility-tools',
        label: 'Utility Tools',
        icon: 'build',
        linkComponent: Link,
      },
      {
        icon: 'visibility',
        link: '/visual-regression',
        label: 'Visual Regression',
        linkComponent: Link,
      },
      // {
      //   icon: 'data_object',
      //   link: '/design-to-code',
      //   label: 'Design to Code',
      //   className: 'beta-title-menu',
      //   linkComponent: Link,
      // },
    ],
  };

  const aboutDevXGroup = {
    label: 'About Dev-X',
    icon: 'more',
    separator: true,

    children: [
      {
        icon: 'help_outline',
        link: '/help',
        label: 'User Guide',
        linkComponent: Link,
      },
      {
        icon: 'feedback',
        link: '/feedback',
        label: 'Feedback',
        linkComponent: Link,
      },
      {
        icon: 'note',
        link: '/release-notes',
        label: 'Release Notes',
        linkComponent: Link,
      },
    ],
  };

  const controlledSideBarItems = [
    {
      label: 'AI in SDLC',
      link: '/ai-in-sdlc',
      iconComponent: <EllisIcon />,
      linkComponent: Link,
    },
    {
      label: 'Permit to build (P2B)',
      link: '/manage-permit-build',
      iconComponent: <PermitBuildIcon />,
      linkComponent: Link,
    },
    {
      label: 'Services & Tools',
      link: '/toolbox',
      icon: 'home_repair_service',
      linkComponent: Link,
    },
    {
      label: 'Information Exchange',
      link: '/information-exchange',
      icon: 'question_answer',
      linkComponent: Link,
    },
    {
      label: 'QE Agents',
      iconComponent: <QEAgentsIcon />,
      children: [
        {
          label: 'QE Score Agent',
          link: '/qe-score',
          iconComponent: <QEScoreIcon />,
          linkComponent: Link,
        },
        {
          label: 'IQE Agent',
          link: '/iqe-agent',
          iconComponent: <IQEAgentMenuIcon />,
          linkComponent: Link,
        },
      ],
    },
    {
      label: 'Data Insights',
      link: '/data-insights',
      icon: 'insights',
      linkComponent: Link,
    },
    {
      label: 'POC Hub',
      link: '/poc-hub',
      icon: 'lightbulb_circle', // Similar to POC icon
      linkComponent: Link,
    },
    {
      label: 'Provisioning Center',
      link: '/provisioning-center',
      iconComponent: <ProvisioningCenterIcon />,
      linkComponent: Link,
    },
    {
      label: 'Prompt Store',
      link: '/prompt-store',
      iconComponent: <PromptStoreIcon />,
      linkComponent: Link,
    },
    {
      label: 'GitHub Cost Center',
      link: '/github-cost-center',
      iconComponent: <GitHubCostCenterIcon />,
      linkComponent: Link,
    },
    {
      label: 'AI Store',
      name: 'AI Assistants Catalog',
      link: '/ai-store',
      icon: 'auto_awesome',
      linkComponent: Link,
    },
    {
      label: 'Ellis AI D&T Chat',
      link: '/ellis-ai-dnt-chat',
      iconComponent: <EllisIcon />,
      linkComponent: Link,
    },
  ];

  const { sidebarFeatures, sidebarFeatureChildren } = useFeatureFlags(
    controlledSideBarItems,
  );

  const navItems = [
    { label: 'Home', link: '/catalog', icon: 'home', linkComponent: Link },
    { ...allAssetsGroup },
    {
      label: 'My Assets',
      link: '/catalog/my-assets',
      icon: 'folder_open',
      linkComponent: Link,
    },
    { ...developerUtilities },
    ...buildControlledSideBarItems(
      controlledSideBarItems,
      sidebarFeatures,
      sidebarFeatureChildren,
    ),
    { ...aboutDevXGroup },
  ];

  const AdminButton = () => {
    const navigate = useNavigate();
    const isOpen = useMutationObserver(sidebarPinned);

    const navigateToAdmin = () => {
      navigate('admin');
    };
    return (
      <>
        <div className={classes.buttonContainer}>
          {isOpen ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AdminPanelSettingsIcon />}
              onClick={navigateToAdmin}
              fullWidth
            >
              Admin
            </Button>
          ) : (
            <Button
              onClick={navigateToAdmin}
              variant="contained"
              color="primary"
              className={classes.mobileButton}
            >
              <AdminPanelSettingsIcon />
            </Button>
          )}
        </div>
      </>
    );
  };

  const SearchModalWrapper = React.memo(() => {
    return (
      <SidebarSearchModal>
        {({ toggleModal }) => <SearchModal toggleModal={toggleModal} />}
      </SidebarSearchModal>
    );
  });

  const Header = () => {
    return (
      <>
        <SidebarLogo isPinned={sidebarPinned} />
        <SearchModalWrapper />
      </>
    );
  };

  const Footer = () => {
    const isOpen = useMutationObserver(sidebarPinned);
    return (
      <>
        {(userContext?.isAdmin || userContext?.isDomainAdmin) && (
          <AdminButton />
        )}
        <div className="username-container">
          <BsLink
            to="/settings"
            underline="none"
            component={NavLink}
            className={classes.leftAvatar}
          >
            <Avatar displayName={userName} picture={profile?.picture} />
            <span>{isOpen ? userName : null}</span>
            {/* <span className="username"> {userName} </span> */}
          </BsLink>
        </div>
      </>
    );
  };

  const handleToggle = (expanded: boolean) => {
    //  setOpen(expanded);
    if (isMobile && expanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const handlePinChange = useCallback(
    (pinned: boolean) => {
      toggleSidebarPinState();
      console.log(pinned);
    },
    [toggleSidebarPinState],
  );

  // Collapse sidebar when on ellis-ai-dnt-chat route
  useEffect(() => {
    if (location.pathname === '/ellis-ai-dnt-chat') {
      setSidebarPinned(false);
    } else {
      setSidebarPinned(isPinned);
      document.documentElement.removeAttribute('data-ellis-sidebar-open');
    }
  }, [location.pathname, isPinned]);

  return (
    <div className={classes.rootDiv}>
      {devxHeader &&
        createPortal(
          <IconButton
            onClick={() => toggleSidebarPinState()}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>,
          devxHeader,
        )}

      <EmeraldSideNavigation
        items={navItems}
        nestedViewEvent="hover"
        header={<Header />}
        footer={<Footer />}
        pinExpansion={sidebarPinned}
        activePath={location.pathname}
        onToggle={handleToggle}
        onPinChange={handlePinChange}
        expandOnHover={location.pathname !== '/ellis-ai-dnt-chat'}
        // {...(isMobile && {
        //   closeOutsideClick: true,
        //   onClose: () => onClose(),
        // })}
      ></EmeraldSideNavigation>
    </div>
  );
};

export default SideNav;
